import {
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns/format'
import { addMonths } from 'date-fns/addMonths'
import { enAU } from 'date-fns/locale/en-AU'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

/** Australian calendar locale for react-datepicker (week labels, etc.); keeps behaviour aligned with DD/MM/YYYY text field. */
registerLocale('en-AU', enAU)
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './ui'
import {
  formatDateToDdMmYyyy,
  formatDdMmYyyyAsUserTypes,
  isValidDdMmYyyyCalendarDate,
  parseDdMmYyyyToDate,
  PORTAL_DATE_MIN,
  startOfTodayLocal,
} from '../utils/dobDateInput'

/**
 * Default layout for date fields in form grids. Border, padding, and typography are applied inside
 * {@link DateInput} so every screen matches and the text + calendar control shares one clean box.
 */
export const DATE_INPUT_FIELD_CLASS = 'w-full min-w-0'

const TEXT_BLOCK =
  'px-[12px] py-[8px] font-body font-regular text-md leading-md text-text-primary-900 placeholder:text-text-placeholder'

const FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid'

/** Single full-width field (no calendar button). */
const STANDALONE_CLASS = `rounded-md border border-solid border-border-primary bg-background-primary shadow-xs ${TEXT_BLOCK} ${FOCUS} focus-visible:ring-offset-0 w-full min-w-0`

/** Outer shell: one border for text + icon (avoids seam/glitches between segments). */
const COMPOUND_SHELL_CLASS =
  'flex w-full min-w-0 items-stretch overflow-hidden rounded-md border border-solid border-border-primary bg-background-primary shadow-xs'

/** Text segment inside compound control. */
const COMPOUND_INPUT_CLASS = `min-w-0 flex-1 border-0 bg-transparent shadow-none rounded-none ${TEXT_BLOCK} ${FOCUS} focus-visible:ring-inset`

/** Positions where a segment boundary slash may appear in a formatted DD/MM/YYYY string. */
const SLASH_BOUNDARY_INDICES = [2, 5] as const

type Props = {
  id: string
  value: string
  onValueChange: (next: string) => void
  /** Layout / utility classes on the control root (e.g. {@link DATE_INPUT_FIELD_CLASS}). */
  className?: string
  placeholder?: string
  name?: string
  /** When false, only the typed DD/MM/YYYY field is shown (no popover calendar). */
  showCalendar?: boolean
  minDate?: Date
  maxDate?: Date
  /** Highlights this day in the calendar (e.g. policy cover start on FNOL when & where). */
  policyStartHighlightDate?: Date
  /** Legend copy under the calendar when {@link policyStartHighlightDate} is set. */
  policyStartLegendLabel?: string
  /** When the field is empty, highlight maxDate muted (latest selectable — usually today) in the calendar. */
  focusLatestSelectableDayWhenEmpty?: boolean
  /** Legend copy for {@link focusLatestSelectableDayWhenEmpty}'s muted anchor (shown beside policy legend when both apply). */
  currentDateLegendLabel?: string
  /** Optional id(s) of elements describing the input (e.g. inline validation). */
  ariaDescribedBy?: string
  /** When true, sets `aria-invalid` on the text field. */
  ariaInvalid?: boolean
}

function joinClasses(...parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(' ')
}

/** Inclusive calendar years allowed for the popover year select (respects min/max dates). */
export function buildCalendarYearRange(minDay: Date, maxDay: Date): number[] {
  const minYear = minDay.getFullYear()
  const maxYear = maxDay.getFullYear()
  const years: number[] = []
  for (let y = minYear; y <= maxYear; y++) {
    years.push(y)
  }
  return years
}

function startOfMonthLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** Same local calendar day (ignores time-of-day). */
export function isSameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const POLICY_START_DAY_CLASS = 'react-datepicker__day--policy-start'
/** When the field is empty, highlights maxDate (normally today) — not the committed selection — and pairs with legend. */
const FNOL_LATEST_EMPTY_ANCHOR_DAY_CLASS = 'react-datepicker__day--fnol-latest-empty-anchor'
const DEFAULT_POLICY_START_LEGEND_LABEL = 'Policy start date'
const DEFAULT_CURRENT_DATE_LEGEND_LABEL = 'Current date'

/** Keep calendar view month within min/max (by month). */
function clampMonthToRange(view: Date, minDay: Date, maxDay: Date): Date {
  const viewStart = startOfMonthLocal(view).getTime()
  const minStart = startOfMonthLocal(minDay).getTime()
  const maxStart = startOfMonthLocal(maxDay).getTime()
  if (viewStart < minStart) return startOfMonthLocal(minDay)
  if (viewStart > maxStart) return startOfMonthLocal(maxDay)
  return startOfMonthLocal(view)
}

function canShiftCalendarMonth(view: Date, delta: number, minDay: Date, maxDay: Date): boolean {
  return (
    clampMonthToRange(addMonths(view, delta), minDay, maxDay).getTime() !==
    startOfMonthLocal(view).getTime()
  )
}

type CalendarPopoverToolbarProps = {
  view: Date
  years: number[]
  yearSelectId: string
  minDay: Date
  maxDay: Date
  onViewChange: (next: Date) => void
}

/** Year/month controls live outside react-datepicker so its year widget never renders. */
function CalendarPopoverToolbar({
  view,
  years,
  yearSelectId,
  minDay,
  maxDay,
  onViewChange,
}: CalendarPopoverToolbarProps) {
  const monthLabel = format(view, 'MMMM', { locale: enAU })
  const prevDisabled = !canShiftCalendarMonth(view, -1, minDay, maxDay)
  const nextDisabled = !canShiftCalendarMonth(view, 1, minDay, maxDay)

  return (
    <div className="date-picker-calendar-toolbar flex items-center justify-center gap-[10px] border-b border-border-secondary px-[12px] py-[10px]">
      <button
        type="button"
        onClick={() => onViewChange(clampMonthToRange(addMonths(view, -1), minDay, maxDay))}
        disabled={prevDisabled}
        aria-label="Previous month"
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-text-secondary-700 enabled:hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid"
      >
        <ChevronLeftIcon className="size-5" aria-hidden />
      </button>
      <select
        id={yearSelectId}
        aria-label="Year"
        value={view.getFullYear()}
        onChange={(e) => {
          const y = parseInt(e.target.value, 10)
          onViewChange(clampMonthToRange(new Date(y, view.getMonth(), 1), minDay, maxDay))
        }}
        className="date-picker-calendar-year-select"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <span className="min-w-[4.5rem] shrink-0 text-center font-body text-sm font-semibold leading-sm text-text-primary-900">
        {monthLabel}
      </span>
      <button
        type="button"
        onClick={() => onViewChange(clampMonthToRange(addMonths(view, 1), minDay, maxDay))}
        disabled={nextDisabled}
        aria-label="Next month"
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-text-secondary-700 enabled:hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid"
      >
        <ChevronRightIcon className="size-5" aria-hidden />
      </button>
    </div>
  )
}

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9'
}

/** Count digit characters strictly before `index` in a formatted DD/MM/YYYY string. */
function countDigitsBefore(formatted: string, index: number): number {
  let count = 0
  const end = Math.min(index, formatted.length)
  for (let i = 0; i < end; i++) {
    if (isDigit(formatted[i])) count++
  }
  return count
}

/** Map a digit offset (0–8) to a caret index in the formatted string. */
function caretForDigitOffset(formatted: string, digitOffset: number): number {
  if (digitOffset <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (isDigit(formatted[i])) {
      seen++
      if (seen >= digitOffset) return i + 1
    }
  }
  return formatted.length
}

/** Advance the caret past a boundary slash when one was newly inserted while typing. */
function autoAdvancePastNewSlash(
  oldFormatted: string,
  newFormatted: string,
  caret: number
): number {
  let next = caret
  for (const slashIndex of SLASH_BOUNDARY_INDICES) {
    if (
      newFormatted.length > slashIndex &&
      newFormatted[slashIndex] === '/' &&
      oldFormatted[slashIndex] !== '/' &&
      next === slashIndex
    ) {
      next = slashIndex + 1
    }
  }
  return next
}

/**
 * When the browser replaces a selection, rebuild the raw digit string from the previous value
 * plus the typed segment so controlled formatting does not garble the field.
 */
function buildDigitsWithSelectionReplacement(
  oldFormatted: string,
  browserValue: string,
  selStart: number,
  selEnd: number
): string {
  const prevDigits = oldFormatted.replace(/\D/g, '')
  const digitStart = countDigitsBefore(oldFormatted, selStart)
  const digitEnd = countDigitsBefore(oldFormatted, selEnd)
  const browserDigits = browserValue.replace(/\D/g, '').slice(0, 8)

  const before = prevDigits.slice(0, digitStart)
  const after = prevDigits.slice(digitEnd)

  if (browserDigits.startsWith(before) && browserDigits.endsWith(after)) {
    return browserDigits
  }

  const middleLen = browserDigits.length - before.length - after.length
  if (middleLen >= 0) {
    const middle = browserDigits.slice(before.length, before.length + middleLen)
    return (before + middle + after).slice(0, 8)
  }

  return browserDigits
}

/** Build the next raw digit string from a controlled edit, using caret position when the browser value is ambiguous. */
function buildDigitsFromEdit(
  oldFormatted: string,
  browserValue: string,
  selStart: number,
  selEnd: number
): string {
  const oldDigits = oldFormatted.replace(/\D/g, '')
  const digitStart = countDigitsBefore(oldFormatted, selStart)

  if (selStart !== selEnd) {
    return buildDigitsWithSelectionReplacement(oldFormatted, browserValue, selStart, selEnd)
  }

  const browserDigits = browserValue.replace(/\D/g, '').slice(0, 8)
  const delta = browserDigits.length - oldDigits.length

  if (delta === 1 && oldDigits.length < 8) {
    const inserted = browserDigits[digitStart]
    if (inserted !== undefined && isDigit(inserted)) {
      return (oldDigits.slice(0, digitStart) + inserted + oldDigits.slice(digitStart)).slice(
        0,
        8
      )
    }
  }

  if (
    delta === 0 &&
    digitStart < oldDigits.length &&
    browserDigits.length === oldDigits.length &&
    browserDigits !== oldDigits
  ) {
    const replaced = browserDigits[digitStart]
    if (replaced !== undefined && isDigit(replaced)) {
      return (
        oldDigits.slice(0, digitStart) + replaced + oldDigits.slice(digitStart + 1)
      ).slice(0, 8)
    }
  }

  if (delta === -1 && digitStart > 0) {
    return oldDigits.slice(0, digitStart - 1) + oldDigits.slice(digitStart)
  }

  return browserDigits
}

/** Derive the post-edit digit offset where the caret should sit. */
function digitCaretTargetAfterEdit(
  oldFormatted: string,
  newDigits: string,
  selStart: number,
  selEnd: number
): number {
  const oldDigits = oldFormatted.replace(/\D/g, '')
  const digitsBefore = countDigitsBefore(oldFormatted, selStart)

  if (selStart !== selEnd) {
    const digitEnd = countDigitsBefore(oldFormatted, selEnd)
    const replacedLen = digitEnd - digitsBefore
    const insertedLen = newDigits.length - oldDigits.length + replacedLen
    return Math.max(0, Math.min(digitsBefore + insertedLen, newDigits.length))
  }

  const delta = newDigits.length - oldDigits.length
  if (delta !== 0) {
    return Math.max(0, Math.min(digitsBefore + delta, newDigits.length))
  }
  const digitStart = countDigitsBefore(oldFormatted, selStart)
  if (digitStart < oldDigits.length && newDigits !== oldDigits) {
    return Math.min(digitStart + 1, newDigits.length)
  }
  return digitsBefore
}

/** When backspace lands on a segment slash, delete the appropriate digit from the raw stream. */
function deleteDigitWhenBackspacingOnSlash(
  formatted: string,
  caretPos: number
): { digits: string; digitCaretTarget: number } | null {
  if (caretPos <= 0 || formatted[caretPos - 1] !== '/') return null
  const digits = formatted.replace(/\D/g, '')
  let digitIdx = countDigitsBefore(formatted, caretPos)
  if (digitIdx >= digits.length) {
    digitIdx -= 1
  }
  if (digitIdx < 0) {
    return { digits: '', digitCaretTarget: 0 }
  }
  return {
    digits: digits.slice(0, digitIdx) + digits.slice(digitIdx + 1),
    digitCaretTarget: digitIdx,
  }
}

/** Delete the digit immediately before the caret (or the selected digit range). */
function deleteDigitsBeforeCaret(
  formatted: string,
  selStart: number,
  selEnd: number
): { digits: string; digitCaretTarget: number } {
  const oldDigits = formatted.replace(/\D/g, '')
  const digitStart = countDigitsBefore(formatted, selStart)
  const digitEnd = countDigitsBefore(formatted, selEnd)
  if (digitStart === digitEnd && digitStart === 0 && oldDigits.length === 0) {
    return { digits: '', digitCaretTarget: 0 }
  }
  if (digitStart === digitEnd && digitStart > 0) {
    return {
      digits: oldDigits.slice(0, digitStart - 1) + oldDigits.slice(digitStart),
      digitCaretTarget: digitStart - 1,
    }
  }
  return {
    digits: oldDigits.slice(0, digitStart) + oldDigits.slice(digitEnd),
    digitCaretTarget: digitStart,
  }
}

/** Normalise pasted text: strip separators and reorder ISO YYYYMMDD to DDMMYYYY when detected. */
function normalisePastedDateDigits(text: string): string {
  const stripped = text.replace(/[-.\s]/g, '')
  const digitsOnly = stripped.replace(/\D/g, '')
  if (/^(19|20)\d{6}$/.test(digitsOnly)) {
    return (
      digitsOnly.slice(6, 8) + digitsOnly.slice(4, 6) + digitsOnly.slice(0, 4)
    )
  }
  return digitsOnly
}

/**
 * Universal DD/MM/YYYY field with auto-inserted slashes and an optional inline calendar.
 * Caret position is tracked explicitly across formatting so typing never jumps to the end.
 *
 * The calendar uses `locale="en-AU"` and `dateFormat="dd/MM/yyyy"` (date-fns tokens: day/month/year).
 * Do not switch to US-style `MM/dd/yyyy` — that reintroduces month/day ambiguity with typed dates.
 */
export default function DateInput({
  id,
  value,
  onValueChange,
  className = DATE_INPUT_FIELD_CLASS,
  placeholder = 'DD/MM/YYYY',
  name,
  showCalendar = true,
  minDate,
  maxDate,
  policyStartHighlightDate,
  policyStartLegendLabel = DEFAULT_POLICY_START_LEGEND_LABEL,
  focusLatestSelectableDayWhenEmpty = false,
  currentDateLegendLabel = DEFAULT_CURRENT_DATE_LEGEND_LABEL,
  ariaDescribedBy,
  ariaInvalid,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const pendingCaretRef = useRef<number | null>(null)
  const selectionRef = useRef({ start: 0, end: 0 })
  /** Selection captured on keydown before the browser mutates the field (for replacement typing). */
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null)
  /** True when focus came from a pointer click — skip select-all so users can edit in place. */
  const focusViaPointerRef = useRef(false)
  const [open, setOpen] = useState(false)
  const [calendarView, setCalendarView] = useState(() => startOfMonthLocal(new Date()))
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(
    null
  )
  const yearSelectId = `${id}-calendar-year`
  const min = minDate ?? PORTAL_DATE_MIN
  const max = maxDate ?? startOfTodayLocal()
  const calendarYears = useMemo(
    () => buildCalendarYearRange(min, max),
    [min.getTime(), max.getTime()]
  )
  const selectedDate = parseDdMmYyyyToDate(value)
  /** Only a chosen / typed calendar date may use react-datepicker `--selected`; never maxDate anchor while empty (avoids “preselected” look). */
  const calendarSelectedDate = selectedDate ?? null

  const fnolLatestSelectableAnchorDay =
    focusLatestSelectableDayWhenEmpty && value === '' ? max : null

  const resolveCalendarView = useCallback((): Date => {
    const defaultView = value === '' && focusLatestSelectableDayWhenEmpty ? max : new Date()
    return clampMonthToRange(selectedDate ?? defaultView, min, max)
  }, [
    value,
    focusLatestSelectableDayWhenEmpty,
    min.getTime(),
    max.getTime(),
    selectedDate?.getTime() ?? null,
  ])

  const syncPopoverPosition = useCallback(() => {
    const anchor = wrapperRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    setPopoverPosition({
      top: rect.bottom + 4,
      left: rect.left,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    setCalendarView((prev) => {
      const next = resolveCalendarView()
      return prev.getTime() === next.getTime() ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset month when popover opens
  }, [open])

  const syncSelectionRef = (el: HTMLInputElement) => {
    selectionRef.current = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
    }
  }

  const commitFormatted = (
    formatted: string,
    oldFormatted: string,
    digitCaretTarget: number
  ) => {
    let caret = caretForDigitOffset(formatted, digitCaretTarget)
    caret = autoAdvancePastNewSlash(oldFormatted, formatted, caret)
    pendingCaretRef.current = Math.min(caret, formatted.length)
    onValueChange(formatted)
  }

  const applyDigits = (
    digits: string,
    oldFormatted: string,
    digitCaretTarget: number
  ) => {
    commitFormatted(formatDdMmYyyyAsUserTypes(digits), oldFormatted, digitCaretTarget)
  }

  useLayoutEffect(() => {
    const pos = pendingCaretRef.current
    if (pos === null) return
    pendingCaretRef.current = null
    const node = inputRef.current
    if (!node) return
    node.setSelectionRange(pos, pos)
    syncSelectionRef(node)
  }, [value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.currentTarget
    const oldFormatted = value
    const pending = pendingSelectionRef.current
    pendingSelectionRef.current = null
    const selStart = pending?.start ?? selectionRef.current.start
    const selEnd = pending?.end ?? selectionRef.current.end

    const digits = buildDigitsFromEdit(oldFormatted, el.value, selStart, selEnd)

    const digitCaretTarget = digitCaretTargetAfterEdit(
      oldFormatted,
      digits,
      selStart,
      selEnd
    )
    applyDigits(digits, oldFormatted, digitCaretTarget)
  }

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const el = e.currentTarget
    syncSelectionRef(el)

    if (
      e.key === 'Backspace' &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey
    ) {
      e.preventDefault()
      const pos = el.selectionStart ?? 0
      const end = el.selectionEnd ?? 0

      if (pos !== end) {
        const { digits, digitCaretTarget } = deleteDigitsBeforeCaret(value, pos, end)
        applyDigits(digits, value, digitCaretTarget)
        return
      }

      if (pos > 0 && value[pos - 1] === '/') {
        const slashDelete = deleteDigitWhenBackspacingOnSlash(value, pos)
        if (slashDelete) {
          applyDigits(slashDelete.digits, value, slashDelete.digitCaretTarget)
        }
        return
      }

      const { digits, digitCaretTarget } = deleteDigitsBeforeCaret(value, pos, end)
      applyDigits(digits, value, digitCaretTarget)
      return
    }

    if (e.key === 'Delete' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      pendingSelectionRef.current = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      }
      return
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      pendingSelectionRef.current = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      }
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedDigits = normalisePastedDateDigits(
      e.clipboardData.getData('text/plain')
    )
    if (!pastedDigits) return

    const { start, end } = selectionRef.current
    const oldDigits = value.replace(/\D/g, '')
    const digitStart = countDigitsBefore(value, start)
    const digitEnd = countDigitsBefore(value, end)
    const merged =
      start !== end
        ? oldDigits.slice(0, digitStart) + pastedDigits + oldDigits.slice(digitEnd)
        : oldDigits.slice(0, digitStart) + pastedDigits + oldDigits.slice(digitStart)

    const digits = merged.replace(/\D/g, '').slice(0, 8)
    applyDigits(
      digits,
      value,
      Math.min(digitStart + pastedDigits.length, digits.length)
    )
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    syncSelectionRef(e.currentTarget)
    if (isValidDdMmYyyyCalendarDate(value) && !focusViaPointerRef.current) {
      e.target.select()
      selectionRef.current = { start: 0, end: value.length }
    }
    focusViaPointerRef.current = false
  }

  const policyStartDayClassName = useCallback(
    (date: Date) =>
      policyStartHighlightDate && isSameLocalCalendarDay(date, policyStartHighlightDate)
        ? POLICY_START_DAY_CLASS
        : '',
    [policyStartHighlightDate]
  )

  const fnolLatestSelectableDayClassName = useCallback(
    (date: Date) =>
      fnolLatestSelectableAnchorDay &&
      isSameLocalCalendarDay(date, fnolLatestSelectableAnchorDay) &&
      !(
        policyStartHighlightDate &&
        isSameLocalCalendarDay(date, policyStartHighlightDate)
      )
        ? FNOL_LATEST_EMPTY_ANCHOR_DAY_CLASS
        : '',
    [policyStartHighlightDate, fnolLatestSelectableAnchorDay]
  )

  const calendarDayClassName =
    policyStartHighlightDate || fnolLatestSelectableAnchorDay
      ? (date: Date) =>
          joinClasses(policyStartDayClassName(date), fnolLatestSelectableDayClassName(date))
      : undefined

  const showPolicyStartLegend = policyStartHighlightDate != null
  const showCurrentDateLegend = fnolLatestSelectableAnchorDay != null

  /** Commits a calendar day to the text field and closes the popover. */
  const handleCalendarDayPick = (d: Date | null) => {
    if (!d) return
    onValueChange(formatDateToDdMmYyyy(d))
    setOpen(false)
  }

  useLayoutEffect(() => {
    if (!open) {
      setPopoverPosition(null)
      return
    }
    syncPopoverPosition()
    window.addEventListener('scroll', syncPopoverPosition, true)
    window.addEventListener('resize', syncPopoverPosition)
    return () => {
      window.removeEventListener('scroll', syncPopoverPosition, true)
      window.removeEventListener('resize', syncPopoverPosition)
    }
  }, [open, syncPopoverPosition])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return
      }
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const triggerId = `${id}-calendar-trigger`

  const inputProps = {
    ref: inputRef,
    id,
    type: 'text' as const,
    name,
    inputMode: 'numeric' as const,
    autoComplete: 'off' as const,
    placeholder,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
    onFocus: handleFocus,
    onMouseDown: () => {
      focusViaPointerRef.current = true
    },
    onSelect: (e: SyntheticEvent<HTMLInputElement>) =>
      syncSelectionRef(e.currentTarget),
    onClick: (e: ReactMouseEvent<HTMLInputElement>) =>
      syncSelectionRef(e.currentTarget),
    ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
    ...(ariaInvalid ? { 'aria-invalid': true as const } : {}),
  }

  if (!showCalendar) {
    return <input {...inputProps} className={joinClasses(STANDALONE_CLASS, className)} />
  }

  const calendarPopover =
    open && popoverPosition ? (
      <div
        ref={popoverRef}
        id={`${id}-calendar-popover`}
        className="date-picker-calendar fixed z-[100] rounded-lg border border-border-primary bg-background-primary shadow-lg"
        style={{ top: popoverPosition.top, left: popoverPosition.left }}
        role="dialog"
        aria-label="Choose date"
      >
        <CalendarPopoverToolbar
          view={calendarView}
          years={calendarYears}
          yearSelectId={yearSelectId}
          minDay={min}
          maxDay={max}
          onViewChange={setCalendarView}
        />
        <DatePicker
          inline
          locale="en-AU"
          selected={calendarSelectedDate}
          openToDate={calendarView}
          onMonthChange={setCalendarView}
          onYearChange={setCalendarView}
          onSelect={handleCalendarDayPick}
          minDate={min}
          maxDate={max}
          dateFormat="dd/MM/yyyy"
          calendarStartDay={1}
          showYearDropdown={false}
          dayClassName={calendarDayClassName}
          /**
           * FNOL empty-anchor highlight is visual-only; avoid react-datepicker also marking that
           * day `--keyboard-selected` (same cell would carry both classes and fail UX/tests).
           */
          disabledKeyboardNavigation={fnolLatestSelectableAnchorDay != null}
          renderCustomHeader={() => <span className="sr-only">Calendar days</span>}
        />
        {showPolicyStartLegend || showCurrentDateLegend ? (
          <div className="date-picker-calendar-legends" role="presentation">
            {showPolicyStartLegend ? (
              <div className="date-picker-calendar-legend-row">
                <span className="date-picker-calendar-legend-swatch" aria-hidden="true" />
                <span>{policyStartLegendLabel}</span>
              </div>
            ) : null}
            {showCurrentDateLegend ? (
              <div className="date-picker-calendar-legend-row">
                <span className="date-picker-calendar-legend-swatch-current" aria-hidden="true" />
                <span>{currentDateLegendLabel}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    ) : null

  return (
    <>
      <div ref={wrapperRef} className={joinClasses('relative', className)}>
        <div className={COMPOUND_SHELL_CLASS}>
          <input {...inputProps} className={COMPOUND_INPUT_CLASS} />
          <button
            type="button"
            id={triggerId}
            aria-label="Open calendar"
            aria-expanded={open}
            aria-controls={`${id}-calendar-popover`}
            onClick={() => {
              if (open) {
                setOpen(false)
                return
              }
              setCalendarView(resolveCalendarView())
              setOpen(true)
            }}
            className={joinClasses(
              'inline-flex shrink-0 items-center justify-center border-0 border-l border-solid border-border-primary bg-background-primary px-[10px] text-text-secondary-700 transition-colors hover:bg-background-tertiary',
              FOCUS,
              'focus-visible:ring-inset'
            )}
          >
            <CalendarIcon className="size-5 text-text-tertiary-600" aria-hidden />
          </button>
        </div>
      </div>
      {typeof document !== 'undefined' && calendarPopover
        ? createPortal(calendarPopover, document.body)
        : null}
    </>
  )
}
